import React, { useCallback, useEffect } from 'react';
import { useEmailNotifications } from '../hooks/useNotifications';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { getEmailCounts } from '../services/email';
import { isAuthenticated } from '../services/auth';
import { useNavigate } from 'react-router-dom';

/**
 * NotificationManager - Manages browser and push notifications for new emails
 *
 * This component:
 * - Requests notification permission on first load
 * - Polls for new emails every 60 seconds
 * - Shows browser notifications when new emails arrive (only if app is not focused)
 * - Registers for iOS/Android push notifications
 * - Handles notification taps to navigate to email
 */
const NotificationManager: React.FC = () => {
  // Only enable notifications if user is authenticated
  const enabled = isAuthenticated();
  const navigate = useNavigate();

  // Get current unread email count
  const getCurrentCount = useCallback(async () => {
    try {
      const counts = await getEmailCounts();
      return counts.total_unread;
    } catch (error) {
      console.error('Error fetching email counts:', error);
      return 0;
    }
  }, []);

  // Handle notification tap - navigate to the email
  const handleNotificationTapped = useCallback((notification: { data?: Record<string, unknown> }) => {
    if (notification.data?.email_id) {
      navigate(`/email/${notification.data.email_id}`);
    } else {
      // Default: go to inbox
      navigate('/inbox');
    }
  }, [navigate]);

  // Use the email notifications hook for browser notifications + badge
  useEmailNotifications(getCurrentCount, {
    enabled,
    checkInterval: 60000, // Check every 60 seconds
  });

  // Use push notifications hook for iOS/Android
  const { isSupported, permissionStatus, register } = usePushNotifications({
    enabled,
    onNotificationTapped: handleNotificationTapped,
    onNotificationReceived: (_notification) => {
      // Foreground notifications are shown by the system on iOS
    },
  });

  // Auto-register for push notifications if supported and permission is granted
  useEffect(() => {
    if (enabled && isSupported && permissionStatus === 'prompt') {
      // Request permission after a short delay to avoid overwhelming the user
      const timer = setTimeout(() => {
        register();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [enabled, isSupported, permissionStatus, register]);

  // This component doesn't render anything
  return null;
};

export default NotificationManager;

